<template>
  <div class="pagination tw:flex tw:gap-2">
    <BaseButtonGroup>
      <BaseButton
        v-for="(button, buttonIndex) in paginationButtons"
        :key="buttonIndex"
        :active="button.active"
        active-color="secondary"
        :aria-label="button.ariaLabel"
        bordered
        :data-testid="button.dataTestId"
        :disabled="button.disabled"
        :icon="button.icon"
        :label="button.label"
        new-design
        new-icon
        @click="updatePage(button)"
      />
    </BaseButtonGroup>

    <BaseButtonDropdown
      v-if="!hidePageSizeOptions"
      bordered
      chevron
      class="pagination__page-options"
      close-on-click
      :fallback-placements="['bottom-end']"
      flip
      :label="pageSizeLabel"
      new-design
      new-dropdown
      open-dropdown-button-testid="pagination-page-size-trigger"
      placement="top-end"
    >
      <BaseButtonList
        :options="paginationOptions"
        @update:model-value="changePageSelection"
      />
    </BaseButtonDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseButtonDropdown,
  BaseButtonGroup,
  BaseButtonList,
} from "shared/components/base";

import type {
  BasePageOption,
  BasePaginationButton,
  BasePaginationData,
  BasePaginationProps,
  BasePaginationResult,
} from "./types";

const props = withDefaults(defineProps<BasePaginationProps>(), {
  pageSizeOptions: () => [20, 30, 40, 50],
  maxPages: 6,
  disableNumberButtons: false,
});

const emit = defineEmits<{
  (e: "pagination-changed", pagination: BasePaginationData): void;
}>();

const localPagination = ref<BasePaginationData>({ ...props.pagination });

watch(
  () => props.pagination,
  (newPagination) => {
    localPagination.value = { ...newPagination };
  },
  { deep: true, immediate: true }
);

const pageSizeLabel = computed<string>(() =>
  getLocaleText("pagination.page_size_label", {
    pageSize: localPagination.value.pageSize,
  })
);

const paginationOptions = computed<BasePageOption[]>(() =>
  props.pageSizeOptions
    .filter((pageSize) => pageSize !== localPagination.value.pageSize)
    .map((pageSize) => ({
      dataTestId: `pagination-page-size-option-${pageSize}`,
      label: getLocaleText("pagination.page_size_label", { pageSize }),
      value: pageSize,
    }))
);

const getPagination = (): BasePaginationResult => {
  const max = props.pagination.pageCount;

  let maxPages = Math.max(
    props.maxPages,
    1 +
      (!props.disableEllipses ? 2 : 0) +
      (!props.disableBoundaryNumbers ? 2 : 0)
  );

  let start = 1;
  let end = props.maxPages;
  let boundaryStart = false;
  let boundaryEnd = false;
  let ellipsesStart = false;
  let ellipsesEnd = false;

  if (props.maxPages && maxPages < max) {
    maxPages = 1 + Math.floor(maxPages / 2) * 2;

    start = Math.max(
      1,
      Math.min(
        max - maxPages + 1,
        props.pagination.page - Math.floor(maxPages / 2)
      )
    );

    end = Math.min(max, start + maxPages - 1);

    if (!props.disableBoundaryNumbers) {
      boundaryStart = true;
      start += 1;
    }

    if (
      !props.disableEllipses &&
      start > 1 + (!props.disableBoundaryNumbers ? 1 : 0)
    ) {
      ellipsesStart = true;
      start += 1;
    }

    if (!props.disableBoundaryNumbers) {
      boundaryEnd = true;
      end -= 1;
    }

    if (
      !props.disableEllipses &&
      end < max - (!props.disableBoundaryNumbers ? 1 : 0)
    ) {
      ellipsesEnd = true;
      end -= 1;
    }
  } else {
    end = max;
  }

  return {
    start,
    end,
    boundaryStart,
    boundaryEnd,
    ellipsesStart,
    ellipsesEnd,
    maxPages,
  };
};

const paginationButtons = computed(() => {
  const pagination = getPagination();
  const buttons: BasePaginationButton[] = [];

  buttons.push({
    dataTestId: "pagination-previous-page-button",
    icon: "chevron-left",
    disabled: props.pagination.page === 1,
    page: props.pagination.page - 1,
    ariaLabel: getLocaleText("pagination.previous_page"),
  });

  if (pagination.boundaryStart) {
    buttons.push({
      dataTestId: "pagination-page-button-1",
      page: 1,
      label: 1,
      ariaLabel: 1,
      active: props.pagination.page === 1,
      disabled: shouldDisableButton({ label: 1 }),
    });
  }

  if (pagination.ellipsesStart) {
    buttons.push({
      dataTestId: `pagination-page-button-${pagination.start - 1}`,
      label: "...",
      page: pagination.start - 1,
      ariaLabel: pagination.start - 1,
      disabled: shouldDisableButton({ label: "..." }),
    });
  }

  for (let page = pagination.start; page <= pagination.end; page += 1) {
    buttons.push({
      dataTestId: `pagination-page-button-${page}`,
      page,
      label: page,
      ariaLabel: page,
      active: page === props.pagination.page,
      disabled: shouldDisableButton({ label: page }),
    });
  }

  if (pagination.ellipsesEnd) {
    buttons.push({
      dataTestId: `pagination-page-button-${pagination.end + 1}`,
      label: "...",
      page: pagination.end + 1,
      ariaLabel: pagination.end + 1,
      disabled: shouldDisableButton({ label: "..." }),
    });
  }

  if (pagination.boundaryEnd) {
    buttons.push({
      dataTestId: `pagination-page-button-${props.pagination.pageCount}`,
      page: props.pagination.pageCount,
      label: props.pagination.pageCount,
      ariaLabel: props.pagination.pageCount,
      active: props.pagination.page === props.pagination.pageCount,
      disabled: shouldDisableButton({ label: props.pagination.pageCount }),
    });
  }

  buttons.push({
    dataTestId: "pagination-next-page-button",
    icon: "chevron-right",
    disabled: props.pagination.page === props.pagination.pageCount,
    page: props.pagination.page + 1,
    ariaLabel: getLocaleText("pagination.next_page"),
  });

  return buttons;
});

const changePageSelection = (pageOption: BasePageOption) => {
  const page = 1;

  if (props.pagination.pageSize !== pageOption.value) {
    emit("pagination-changed", {
      ...localPagination.value,
      page,
      pageSize: pageOption.value,
    });
  }
};

const updatePage = (button: BasePaginationButton) => {
  if (button.disabled) {
    return;
  }

  emit("pagination-changed", {
    ...localPagination.value,
    page: button.page,
  });
};

const shouldDisableButton = (button: Partial<BasePaginationButton>) => {
  if (!props.disableNumberButtons) {
    return false;
  }

  return !button.icon;
};
</script>
